import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import SpotifyPlayer from "@/components/SpotifyPlayer";
import SpotifyConnect from "@/components/SpotifyConnect";
import ErrorBoundary from "@/components/ErrorBoundary";
import PINModal from "@/components/PINModal";
import RefreshPasswordModal from "@/components/RefreshPasswordModal";
import { Lock } from "lucide-react";
import { BACKEND_URL, API_URL } from "@/config";

const API = API_URL;

export default function HomePage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [danceWarmups, setDanceWarmups] = useState([]);
  const [radioStations, setRadioStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [refreshClicks, setRefreshClicks] = useState(0);
  const [refreshTimer, setRefreshTimer] = useState(null);
  const [showPINModal, setShowPINModal] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [pendingPlaylist, setPendingPlaylist] = useState(null);
  const [defaultBackgroundVolume, setDefaultBackgroundVolume] = useState(20);
  const [defaultFloorVolume, setDefaultFloorVolume] = useState(35);

  useEffect(() => {
    fetchData();
    
    // Expose playRadio function globally for other components to use
    window.playRadioStation = playRadio;
    
    return () => {
      delete window.playRadioStation;
    };
  }, [defaultBackgroundVolume]); // Re-register when volume changes

  const fetchData = async () => {
    try {
      const [categoriesRes, playlistsRes, warmupsRes, radioRes, volumeRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/spotify/playlists`),
        axios.get(`${API}/dance-warmups`),
        axios.get(`${API}/radio-stations`),
        axios.get(`${API}/settings/volumes`)
      ]);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setSpotifyPlaylists(Array.isArray(playlistsRes.data) ? playlistsRes.data : []);
      setDanceWarmups(Array.isArray(warmupsRes.data) ? warmupsRes.data : []);
      setRadioStations(Array.isArray(radioRes.data) ? radioRes.data : []);
      
      // Set volume defaults from settings
      if (volumeRes.data) {
        setDefaultBackgroundVolume(volumeRes.data.default_background_volume || 20);
        setDefaultFloorVolume(volumeRes.data.default_floor_volume || 35);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
      // Ensure arrays are set even on error
      setCategories([]);
      setSpotifyPlaylists([]);
      setDanceWarmups([]);
      setRadioStations([]);
      setSpotifyPlaylists([]);
      setDanceWarmups([]);
    } finally {
      setLoading(false);
    }
  };

  const playSpotifyPlaylist = (playlist) => {
    // Always check if playlist is explicit and require PIN
    if (playlist.explicit) {
      setPendingPlaylist(playlist);
      setShowPINModal(true);
      return;
    }

    // Play the playlist
    if (spotifyConnected && window.playSpotifyPlaylist) {
      window.playSpotifyPlaylist(playlist.playlistUri);
    } else {
      toast.error('Please connect Spotify first');
    }
  };

  const handlePINSuccess = () => {
    setShowPINModal(false);
    if (pendingPlaylist) {
      // Play the playlist
      if (spotifyConnected && window.playSpotifyPlaylist) {
        window.playSpotifyPlaylist(pendingPlaylist.playlistUri);
      }
      setPendingPlaylist(null);
    }
  };

  const playSpotifyTrack = (trackUrl) => {
    if (!spotifyConnected) {
      toast.error('Please connect Spotify first');
      return;
    }

    if (window.playSpotifyTrack) {
      window.playSpotifyTrack(trackUrl);
    } else {
      toast.error('Spotify player not ready');
    }
  };


  const playRadio = async (station) => {
    try {
      console.log('=== STARTING RADIO PLAYBACK ===', station.name);
      
      // Store current station globally for potential resume
      window.currentRadioStation = station;
      
      // Set flag to block Spotify from updating player
      window.mp3PlaybackActive = true;
      
      // Fade out and pause Spotify first
      if (window.spotifyPlayer) {
        await window.spotifyPlayer.setVolume(0);
        await window.spotifyPlayer.pause();
      }

      // Stop any currently playing audio
      if (window.currentMP3Audio) {
        window.currentMP3Audio.pause();
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      // Update unified player
      console.log('=== SWITCHING PLAYER TO RADIO ===', station.name);
      if (window.updatePlayerTrack) {
        window.updatePlayerTrack({
          name: station.name,
          artist: 'Live Radio',
          image: null
        }, 'radio');
      }

      // Create and play audio
      console.log('Creating Radio Audio object for:', station.streamUrl);
      const audio = new Audio(station.streamUrl);
      
      // Set volume to admin-defined background music default
      const bgVolume = defaultBackgroundVolume / 100;
      audio.volume = bgVolume;
      console.log(`Radio volume set to ${defaultBackgroundVolume}%`);
      
      // Update UnifiedPlayer volume to match
      if (window.updateVolumeSlider) {
        window.updateVolumeSlider(defaultBackgroundVolume);
      }
      
      // Store audio globally
      window.currentMP3Audio = audio;
      console.log('Radio Audio object stored');
      
      // Play audio
      console.log('Attempting to play radio stream...');
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log('Radio stream started successfully');
          toast.success(`Playing ${station.name}`);
        }).catch(err => {
          if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
            console.error('Radio play error:', err);
            toast.error('Failed to play radio');
          }
        });
      }
      
      audio.onpause = () => {
        if (window.updatePlayerPaused) window.updatePlayerPaused(true);
      };
      
      audio.onplay = () => {
        if (window.updatePlayerPaused) window.updatePlayerPaused(false);
      };
      
      audio.onended = () => {
        console.log('Radio stream ended');
        window.mp3PlaybackActive = false;
        if (window.updatePlayerPaused) window.updatePlayerPaused(true);
      };
      
      audio.onerror = (e) => {
        console.error('Radio stream error:', e);
        toast.error('Failed to load radio stream');
        window.mp3PlaybackActive = false;
      };
      
    } catch (error) {
      console.error('Error playing radio:', error);
      toast.error('Failed to play radio');
      window.mp3PlaybackActive = false;
    }
  };


  const playMP3Warmup = async (audioUrl, name) => {
    try {
      console.log('=== STARTING DANCE WARMUP MP3 PLAYBACK ===');
      
      // Store radio state if playing to resume later
      let wasRadioPlaying = false;
      let radioStation = null;
      if (window.currentMP3Audio && !window.currentMP3Audio.paused) {
        // Check if it's a radio stream (no duration = live stream)
        if (isNaN(window.currentMP3Audio.duration) || !isFinite(window.currentMP3Audio.duration)) {
          wasRadioPlaying = true;
          radioStation = window.currentRadioStation; // We'll store this when playing radio
          console.log('Radio was playing, will resume after floor music');
        }
        // Pause current audio (radio or previous MP3)
        window.currentMP3Audio.pause();
        window.currentMP3Audio = null;
      }
      
      // Set flag to block Spotify from updating player
      window.mp3PlaybackActive = true;
      
      // Fade out and pause Spotify first
      if (window.spotifyPlayer) {
        await window.spotifyPlayer.setVolume(0);
        await window.spotifyPlayer.pause();
      }

      // Wait a moment for other audio to stop
      await new Promise(resolve => setTimeout(resolve, 100));

      // Update unified player to show MP3 track
      console.log('=== SWITCHING PLAYER TO DANCE WARMUP MP3 ===', name);
      if (window.updatePlayerTrack) {
        window.updatePlayerTrack({
          name: name,
          artist: 'Dance Warmups',
          image: null
        }, 'mp3');
      }

      // Create and play audio
      console.log('Creating MP3 Audio object for URL:', audioUrl);
      const audio = new Audio(audioUrl);
      
      // Set volume to admin-defined floor music default
      const floorVolume = defaultFloorVolume / 100;
      audio.volume = floorVolume;
      console.log(`MP3 volume set to ${defaultFloorVolume}%`);
      
      // Update UnifiedPlayer volume to match
      if (window.updateVolumeSlider) {
        window.updateVolumeSlider(defaultFloorVolume);
      }
      
      // Store audio globally
      window.currentMP3Audio = audio;
      console.log('MP3 Audio object stored in window.currentMP3Audio');
      
      // Play audio
      console.log('Attempting to play MP3...');
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log('MP3 play() promise resolved successfully');
          toast.success(`Playing ${name}`);
        }).catch(err => {
          if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
            console.error('Audio play error:', err);
            toast.error('Failed to play audio');
          }
        });
      }
      
      audio.onpause = () => {
        if (window.updatePlayerPaused) window.updatePlayerPaused(true);
      };
      
      audio.onplay = () => {
        if (window.updatePlayerPaused) window.updatePlayerPaused(false);
      };
      
      audio.onended = () => {
        console.log('MP3 ended');
        window.mp3PlaybackActive = false;
        if (window.updatePlayerPaused) window.updatePlayerPaused(true);
        
        // Resume radio if it was playing before
        if (wasRadioPlaying && radioStation) {
          console.log('Resuming radio after floor music ended');
          setTimeout(() => {
            playRadio(radioStation);
          }, 1000);
        }
      };
      
      audio.onerror = (e) => {
        console.error('MP3 load error:', e);
        toast.error('Failed to load audio');
        window.mp3PlaybackActive = false;
      };
      
    } catch (error) {
      console.error('Error playing MP3 warmup:', error);
      toast.error('Failed to play warmup');
      window.mp3PlaybackActive = false;
    }
  };

  const handleRefreshClick = () => {
    const newCount = refreshClicks + 1;
    setRefreshClicks(newCount);

    // Clear existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }

    // After 3 clicks, show password modal
    if (newCount >= 3) {
      setShowRefreshModal(true);
      setRefreshClicks(0); // Reset counter
      return;
    }

    // Set timer to reset clicks after 5 seconds
    const timer = setTimeout(() => {
      setRefreshClicks(0);
    }, 5000);
    setRefreshTimer(timer);
  };

  const handleRefreshSuccess = () => {
    setShowRefreshModal(false);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <SpotifyConnect onConnectionChange={setSpotifyConnected} />
      <ErrorBoundary>
        <SpotifyPlayer onConnectionChange={setSpotifyConnected} />
      </ErrorBoundary>
      
      <div className="min-h-screen p-4 pb-24" data-testid="home-page">
        {/* Discreet Refresh Button */}
        <button
          onClick={handleRefreshClick}
          className="fixed top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors z-40 opacity-50 hover:opacity-100"
          data-testid="refresh-button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
        </button>
        
        {/* Header with Logo */}
        <div className="flex justify-center mb-4">
          <img src={logo} alt="Dynamo Gymnastics" className="logo" data-testid="home-logo" />
        </div>

        {/* Two Section Layout */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-4">
          {/* Background Music Section */}
          <div className="glass-card" data-testid="background-music-section">
            <h2 className="text-xl font-bold mb-3 text-center" data-testid="background-music-title">
              Background Music
            </h2>
            <div className="space-y-3">
              {/* Spotify Playlists */}
              {(spotifyPlaylists || []).length === 0 ? (
                <p className="text-center text-lg opacity-80" data-testid="no-playlists-message">
                  No playlists available yet
                </p>
              ) : (
                (spotifyPlaylists || []).map((playlist) => (
                  <button
                    key={playlist.id}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    onClick={() => playSpotifyPlaylist(playlist)}
                    data-testid={`spotify-playlist-${playlist.id}`}
                  >
                    {playlist.explicit && <Lock size={18} />}
                    {playlist.name}
                  </button>
                ))
              )}

              {/* Dance Warmups Separator */}
              {(danceWarmups || []).length > 0 && (
                <div className="border-t-2 border-white/20 pt-3 mt-3">
                  <h3 className="text-lg font-bold mb-2 text-center">
                    Dance Warmups
                  </h3>
                  <div className="space-y-2">
                    {danceWarmups.map((warmup) => (
                      <button
                        key={warmup.id}
                        className="btn-primary w-full text-sm"
                        onClick={() => {
                          if (warmup.type === 'spotify') {
                            playSpotifyTrack(warmup.songUrl);
                          } else if (warmup.type === 'mp3') {
                            const audioUrl = `${BACKEND_URL}/api/dance-warmups/${warmup.id}/audio`;
                            playMP3Warmup(audioUrl, warmup.name);
                          }
                        }}
                        data-testid={`dance-warmup-${warmup.id}`}
                      >
                        {warmup.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Live Radio Separator */}
              {(radioStations || []).length > 0 && (
                <div className="border-t-2 border-white/20 pt-3 mt-3">
                  <h3 className="text-lg font-bold mb-2 text-center">
                    Live Radio
                  </h3>
                  <div className="space-y-2">
                    {radioStations.map((station) => (
                      <button
                        key={station.id}
                        className="btn-primary w-full text-sm"
                        onClick={() => playRadio(station)}
                        data-testid={`radio-station-${station.id}`}
                      >
                        {station.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Floor Music Section */}
          <div className="glass-card" data-testid="floor-music-section">
            <h2 className="text-xl font-bold mb-3 text-center" data-testid="floor-music-title">
              Floor Music
            </h2>
            <div className="space-y-3">
              {(categories || []).length === 0 ? (
                <p className="text-center text-lg opacity-80" data-testid="no-categories-message">
                  No categories available yet
                </p>
              ) : (
                (categories || []).map((category) => (
                  <Link
                    key={category.id}
                    to={`/category/${category.id}`}
                    className="btn-primary w-full block text-center"
                    data-testid={`category-${category.id}`}
                  >
                    {category.name}
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PIN Modal for Explicit Content */}
      {showPINModal && pendingPlaylist && (
        <PINModal
          playlistName={pendingPlaylist.name}
          onClose={() => {
            setShowPINModal(false);
            setPendingPlaylist(null);
          }}
          onSuccess={handlePINSuccess}
        />
      )}

      {/* Refresh Password Modal */}
      {showRefreshModal && (
        <RefreshPasswordModal
          onClose={() => setShowRefreshModal(false)}
          onSuccess={handleRefreshSuccess}
        />
      )}
    </>
  );
}
